import type { Actor } from '../../domain/permissions';
import type { CommandResult, Session, UUID } from '../../domain/types';
import { StorageError } from './blobStore';
import { MockDb, NetworkError } from './MockDb';

/**
 * Session with role and person link re-derived from the tables (approvals may
 * have changed them), and the administrator's role held back unless Admin mode
 * is on for this device. Every permission check in the app and in these
 * repositories reads `session.role`, so this is the single place that decides
 * whether an administrator is currently acting as one.
 */
export function freshSession(db: MockDb, stored: Session | null = db.getSession()): Session | null {
  if (!stored) return null;
  // Administrators are named by email address, so the stored membership follows
  // that list rather than the other way round (domain/types.ts → Governance).
  db.syncAdminEmails();
  const membership = db.membership(stored.accountId);
  if (!membership) return null;
  const link = db.t.person_account_links.find((l) => l.accountId === stored.accountId && l.status === 'active');
  const account = db.t.accounts.find((a) => a.id === stored.accountId);
  const person = link ? db.t.persons.find((p) => p.id === link.personId) : undefined;
  const personId = person ? (person.mergedIntoId ?? person.id) : stored.personId && !link ? null : stored.personId;
  const adminAvailable = membership.role === 'admin';
  return {
    ...stored,
    role: adminAvailable && !db.getAdminMode() ? 'member' : membership.role,
    adminAvailable,
    personId: link ? personId : null,
    kind: account?.kind ?? stored.kind,
    familyId: membership.familyId,
  };
}

export function actorOf(db: MockDb): Actor {
  const session = freshSession(db);
  if (!session) throw new Error('not signed in');
  return {
    session,
    grants: db.t.profile_management_grants.filter((g) => g.helperAccountId === session.accountId && g.status === 'active'),
  };
}

export function tryActor(db: MockDb): Actor | null {
  try {
    return actorOf(db);
  } catch {
    return null;
  }
}

/** Wrap a write so simulated network failures become typed results instead of exceptions. */
export async function guarded<T>(db: MockDb, fn: () => Promise<CommandResult<T>>): Promise<CommandResult<T>> {
  try {
    await db.write();
    return await fn();
  } catch (e) {
    if (e instanceof NetworkError) {
      return { ok: false, kind: 'network', messageKey: e.message === 'offline' ? 'error.offline' : 'error.network', retryable: true };
    }
    if (e instanceof StorageError) return { ok: false, kind: 'network', messageKey: 'error.storage', retryable: true };
    if (e instanceof Error && e.message === 'not signed in') return { ok: false, kind: 'permission', messageKey: 'error.notSignedIn' };
    console.error(e);
    return { ok: false, kind: 'network', messageKey: 'error.unexpected', retryable: false };
  }
}

/**
 * A list handed to the screens is a list of **copies**, never of the rows
 * themselves.
 *
 * The mock changes its rows in place (`story.title = …`), and a screen that is
 * holding the very object that changed cannot tell that it did: to React it is
 * the same story it already drew. That used to be papered over by every row on
 * every screen being drawn again on every change; now that rows are memoised
 * (they have to be — there can be thousands), a row handed the same object
 * rightly does nothing, and an edited title stayed "Untitled" on the family's
 * line until something else happened to redraw it. A real backend hands back
 * fresh objects on every read; so does this. One level deep is enough: a field
 * that changes is replaced, never edited inside.
 */
export const copies = <T extends object>(rows: readonly T[]): T[] => rows.map((r) => ({ ...r }));

export const notFound = <T>(): CommandResult<T> => ({ ok: false, kind: 'not_found', messageKey: 'error.notFound' });
export const denied = <T>(key = 'error.permission'): CommandResult<T> => ({ ok: false, kind: 'permission', messageKey: key });
export const invalid = <T>(key: string, params?: Record<string, string | number>): CommandResult<T> => ({ ok: false, kind: 'validation', messageKey: key, params });
export const okResult = <T>(value: T, extra: { merged?: boolean; queuedOffline?: boolean; noop?: boolean } = {}): CommandResult<T> => ({ ok: true, value, ...extra });

export function isEditorSession(s: Session): boolean {
  return s.role === 'editor' || s.role === 'admin';
}

export function isAdminSession(s: Session): boolean {
  return s.role === 'admin';
}

export function personIdsManagedBy(db: MockDb, accountId: UUID): UUID[] {
  const out: UUID[] = [];
  const link = db.t.person_account_links.find((l) => l.accountId === accountId && l.status === 'active');
  if (link) out.push(link.personId);
  for (const g of db.t.profile_management_grants) if (g.helperAccountId === accountId && g.status === 'active') out.push(g.managedPersonId);
  return out;
}
